import React, { useState, useEffect } from "react";
import { api } from "../lib/api";
import { apiErrorMessage } from "../lib/inventoryApi";
import {
  useRealtimeTable,
  useVisiblePolling,
  REALTIME_DOWN_POLL_MS,
} from "../lib/useRealtimeTable";
import { useAuth } from "../contexts/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { AlertTriangle, Power, CheckCircle2, XCircle, Clock } from "lucide-react";
import { formatDateTime } from "../lib/format";
import { mintaTeks } from "../lib/dialogAplikasi";
import Hint from "../components/etams/Hint";
import SaklarDokuCabang from "../components/etams/SaklarDokuCabang";
import KartuMetodeDoku from "../components/etams/KartuMetodeDoku";

/**
 * K192: kirim SATU alarm uji dari server — membuktikan env Telegram di server benar-benar
 * sampai ke grup alarm (health hanya melaporkan env-nya terisi). Isi pesan tetap; server
 * membatasinya sekali per 5 menit.
 */
function KartuAlarmUji() {
  const [kirim, setKirim] = useState(false);
  const [hasil, setHasil] = useState(null);
  const uji = async () => {
    setKirim(true);
    try {
      const r = await api.post("/alarm/uji");
      setHasil(r.data);
      const tg = r.data?.telegram?.status;
      if (tg === "terkirim") toast.success("Alarm uji terkirim ke Telegram.");
      else toast.error(`Telegram: ${tg || "tidak diketahui"} — ${r.data?.telegram?.sebab || "periksa env server"}`);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal mengirim alarm uji"));
    } finally {
      setKirim(false);
    }
  };
  return (
    <Card className="p-6 space-y-2" data-testid="kartu-alarm-uji">
      <div className="text-sm font-medium">Uji alarm sistem</div>
      <p className="text-sm text-muted-foreground flex items-center gap-1">
        Kirim satu alarm uji ke grup Telegram alarm dan ke notifikasi aplikasi.
        <Hint>Untuk memastikan jalurnya bekerja. Pesannya bertanda "bukan masalah".</Hint>
      </p>
      <div className="flex items-center gap-3">
        <Button variant="outline" size="sm" onClick={uji} disabled={kirim} data-testid="btn-alarm-uji">
          {kirim ? "Mengirim…" : "Kirim alarm uji"}
        </Button>
        {hasil && (
          <span className="text-xs text-muted-foreground">
            Telegram: {hasil.telegram?.status} · Aplikasi: {hasil.notifikasi?.status}
          </span>
        )}
      </div>
    </Card>
  );
}

/**
 * K140 "Owner selalu bisa mencabut delegasi siapa pun" (F1 26 Sep 2026): daftar sesi Bertindak Atas Nama yang
 * masih hidup + tombol Cabut. Token sesi yang dicabut ditolak backend seketika.
 */
function KartuDelegasiAktif() {
  const [items, setItems] = useState(null);
  const muat = async () => {
    try { const r = await api.get("/auth/_dev_impersonate/aktif"); setItems(r.data?.items || []); }
    catch { setItems(null); }   // fitur delegasi mati / bukan Owner — kartu disembunyikan
  };
  useEffect(() => { muat(); }, []);
  if (items === null) return null;
  const cabut = async (id) => {
    try { await api.post(`/auth/_dev_impersonate/cabut/${id}`); toast.success("Delegasi dicabut."); muat(); }
    catch (e) { toast.error(apiErrorMessage(e, "Gagal mencabut delegasi")); }
  };
  return (
    <Card className="p-6 space-y-2" data-testid="kartu-delegasi-aktif">
      <div className="text-sm font-medium">Delegasi aktif (Bertindak Atas Nama)</div>
      {items.length === 0 && <p className="text-sm text-muted-foreground">Tidak ada delegasi yang sedang berjalan.</p>}
      {items.map((d) => (
        <div key={d.switch_id} className="flex flex-wrap items-center justify-between gap-2 rounded border p-2 text-sm">
          <span>{d.pemberi_role_code} → {d.atas_nama_role} · sejak {formatDateTime(d.dimulai_at)} · {d.alasan}</span>
          <Button size="sm" variant="outline" onClick={() => cabut(d.switch_id)} data-testid="btn-cabut-delegasi">Cabut</Button>
        </div>
      ))}
    </Card>
  );
}

/**
 * K208 (12 Okt 2026): DOKU uang muka & pelunasan sejak hari pertama PRODUCTION. Kartu ini menampilkan syarat
 * pengganti (bukti perjalanan sandbox, backup + drill, pernyataan Owner), pemantauan transaksi nyata pertama, dan
 * SAKLAR MATI CEPAT tanpa deploy (butir 4) — mematikan berarti customer bayar lewat transfer, dikonfirmasi Kepala
 * Cabang; pembayaran DOKU yang sudah masuk tetap tercatat.
 */
function KartuDokuDpPelunasan() {
  const [data, setData] = useState(null);
  const [alasan, setAlasan] = useState("");
  const [busy, setBusy] = useState(false);
  const muat = async () => {
    try { const r = await api.get("/payments/syarat-k162"); setData(r.data); }
    catch { setData({ galat: true }); }
  };
  useEffect(() => { muat(); }, []);
  if (!data) return null;
  if (data.galat) {
    return (
      <Card className="p-6 space-y-2" data-testid="kartu-doku-dp-pelunasan">
        <div className="text-sm font-medium">DOKU uang muka &amp; pelunasan</div>
        <p className="text-sm text-muted-foreground">Keadaan jalur DOKU belum bisa dimuat. Coba muat ulang halaman.</p>
      </Card>
    );
  }
  const nyala = data.saklar?.nyala === true;
  const ubah = async () => {
    setBusy(true);
    try {
      await api.post("/payments/saklar-doku", { nyala: !nyala, alasan });
      toast.success(nyala ? "DOKU uang muka & pelunasan DIMATIKAN — customer bayar lewat transfer."
                          : "DOKU uang muka & pelunasan dinyalakan kembali.");
      setAlasan("");
      muat();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal mengubah saklar DOKU"));
    } finally {
      setBusy(false);
    }
  };
  const pantau = data.pemantauan_transaksi_nyata_pertama || {};
  return (
    <Card className="p-6 space-y-3" data-testid="kartu-doku-dp-pelunasan">
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-medium">DOKU uang muka &amp; pelunasan</div>
        <Badge variant={data.berlaku ? "default" : "secondary"} data-testid="doku-dp-berlaku">
          {data.berlaku ? "Berjalan" : "Tidak berjalan"}
        </Badge>
      </div>
      <p className="text-sm text-muted-foreground flex items-center gap-1">
        {data.jalur === "UJI_SANDBOX" ? "Mode UAT — jalur uji DOKU sandbox." : data.jalur === "PRODUKSI"
          ? "Mode PRODUCTION — syarat K208 harus lengkap." : "Jalur DOKU uang muka & pelunasan tidak berjalan di mode ini."}
        <Hint>Bila tidak berjalan, customer tetap bisa bayar lewat transfer yang dikonfirmasi Kepala Cabang.</Hint>
      </p>
      <ul className="space-y-1 text-sm" data-testid="doku-dp-syarat">
        {(data.syarat || []).map((s) => (
          <li key={s.kode} className="flex gap-2">
            {s.terpenuhi ? <CheckCircle2 size={16} className="text-success shrink-0 mt-0.5" />
                         : <XCircle size={16} className="text-danger shrink-0 mt-0.5" />}
            <span>{s.rincian}</span>
          </li>
        ))}
      </ul>
      {pantau.production_aktif_sejak && (
        <p className="text-xs text-muted-foreground" data-testid="doku-dp-pemantauan">
          Transaksi nyata pertama lewat DOKU: {pantau.transaksi
            ? `${pantau.transaksi.invoice_number} · ${formatDateTime(pantau.transaksi.created_at)}`
            : "belum ada"}
        </p>
      )}
      <div className="border-t pt-3 space-y-2">
        <div className="text-sm">
          Saklar: <strong>{nyala ? "menyala" : "mati"}</strong>
          {data.saklar?.alasan ? ` — ${data.saklar.alasan}` : ""}
          {data.saklar?.diubah_at ? ` (${formatDateTime(data.saklar.diubah_at)})` : ""}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input className="max-w-sm" placeholder="Alasan (minimal 10 huruf)" value={alasan}
                 onChange={(e) => setAlasan(e.target.value)} data-testid="doku-saklar-alasan" />
          <Button size="sm" variant={nyala ? "destructive" : "default"} data-testid="doku-saklar-ubah"
                  disabled={busy || alasan.trim().length < 10 || !data.saklar} onClick={ubah}>
            {busy ? "Menyimpan…" : nyala ? "Matikan DOKU uang muka & pelunasan (semua cabang)" : "Nyalakan kembali"}
          </Button>
        </div>
      </div>
    </Card>
  );
}

/**
 * BUSINESS-42 (MANDAT-PRAUAT-26SEP): PRODUCTION tidak pernah kembali ke UAT. Keadaan ketiga DIBEKUKAN:
 * sistem tetap bisa dibaca, input baru berhenti. Masuk: ketik BEKUKAN + alasan. Keluar: ketik PRODUCTION.
 */
function KartuBeku({ isBeku, appMode, refreshAppMode }) {
  const [buka, setBuka] = useState(false);
  const [frasa, setFrasa] = useState("");
  const [alasan, setAlasan] = useState("");
  const [busy, setBusy] = useState(false);
  const frasaWajib = isBeku ? "PRODUCTION" : "BEKUKAN";
  const kirim = async () => {
    setBusy(true);
    try {
      if (isBeku) await api.post("/app-mode/cairkan", { confirmation_phrase: frasa });
      else await api.post("/app-mode/bekukan", { confirmation_phrase: frasa, alasan });
      toast.success(isBeku ? "Sistem kembali ke PRODUCTION." : "Sistem DIBEKUKAN — input baru berhenti.");
      setBuka(false);
      setFrasa("");
      setAlasan("");
      refreshAppMode();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal mengubah mode"));
    } finally {
      setBusy(false);
    }
  };
  const siap = frasa === frasaWajib && (isBeku || alasan.trim().length >= 10) && !busy;
  return (
    <Card className="p-6 space-y-3" data-testid="kartu-beku">
      <div className="text-sm font-medium">{isBeku ? "Sistem sedang DIBEKUKAN" : "Bekukan sistem"}</div>
      <p className="text-sm text-muted-foreground">
        {isBeku
          ? <>Dibekukan {formatDateTime(appMode?.dibekukan_pada)} — alasan: {appMode?.alasan_beku || "-"}. Data tetap bisa dibaca; input baru ditolak. Mencairkan mengembalikan sistem ke PRODUCTION (tidak pernah ke UAT).</>
          : <>Bila ada masalah besar sesudah go-live: bekukan sistem. Data tetap bisa dibaca, semua input baru berhenti sampai Owner mencairkannya. Tidak ada jalan kembali ke mode UAT.</>}
      </p>
      <Dialog open={buka} onOpenChange={setBuka}>
        <DialogTrigger asChild>
          <Button variant={isBeku ? "default" : "destructive"} data-testid="open-beku-dialog">
            {isBeku ? "Cairkan (kembali ke PRODUCTION)" : "Bekukan sistem"}
          </Button>
        </DialogTrigger>
        <DialogContent data-testid="beku-dialog">
          <DialogHeader>
            <DialogTitle>{isBeku ? "Cairkan sistem" : "Bekukan sistem"}</DialogTitle>
            <DialogDescription>
              {isBeku ? "Semua staf bisa kembali memasukkan data." : "Semua staf hanya bisa membaca sampai sistem dicairkan."}
            </DialogDescription>
          </DialogHeader>
          {!isBeku && (
            <div className="space-y-1">
              <label className="text-sm">Alasan (min. 10 karakter):</label>
              <Input data-testid="beku-alasan-input" value={alasan} onChange={(e) => setAlasan(e.target.value)} />
            </div>
          )}
          <div className="space-y-1">
            <label className="text-sm">Ketik <code className="px-1 bg-muted rounded">{frasaWajib}</code> untuk konfirmasi:</label>
            <Input data-testid="beku-phrase-input" value={frasa} onChange={(e) => setFrasa(e.target.value)} placeholder={frasaWajib} />
          </div>
          <DialogFooter>
            <Button data-testid="confirm-beku-btn" variant={isBeku ? "default" : "destructive"} disabled={!siap} onClick={kirim}>
              {busy ? "Memproses…" : "Konfirmasi"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default function Settings() {
  const { appMode, refreshAppMode } = useAuth();
  const [resetOpen, setResetOpen] = useState(false);
  const [prodOpen, setProdOpen] = useState(false);
  const [resetPhrase, setResetPhrase] = useState("");
  const [prodPhrase, setProdPhrase] = useState("");
  const [busy, setBusy] = useState(false);

  // WP-0b GAP-2: detailed reset summary card.
  const [resetSummary, setResetSummary] = useState(null);
  const [showProtected, setShowProtected] = useState(false);
  const [showTruncated, setShowTruncated] = useState(false);

  // WP-0b GAP-3b: pre-flight preview before Production activation.
  const [preflight, setPreflight] = useState(null);
  const [preflightLoading, setPreflightLoading] = useState(false);

  // BUSINESS-42: DIBEKUKAN juga data nyata — Reset & aktivasi tertutup sama seperti PRODUCTION.
  const isProduction = appMode?.mode === "PRODUCTION" || appMode?.mode === "DIBEKUKAN";
  const isBeku = appMode?.mode === "DIBEKUKAN";

  // Jeda polling layar admin. 25 detik MENIRU NotificationsBell, jeda paling
  // longgar yang dipakai repo ini — setelan aplikasi berubah jauh lebih jarang
  // daripada transaksi, dan Owner yang mengubahnya melihat hasilnya seketika
  // dari aksinya sendiri.
  const APP_MODE_POLL_MS = 25000;

  // Sebelumnya channel ini `.subscribe()` telanjang: NOL callback status, jadi
  // CHANNEL_ERROR dan TIMED_OUT lewat tanpa jejak, nol penyambungan ulang, dan
  // layar ini NOL polling — kalau channel putus, peralihan UAT/PRODUCTION tidak
  // pernah terlihat sampai halaman dibuka ulang.
  const rtConnected = useRealtimeTable("app_mode_settings", refreshAppMode);
  useVisiblePolling(
    refreshAppMode,
    rtConnected ? APP_MODE_POLL_MS : REALTIME_DOWN_POLL_MS
  );

  // Load preflight when Production dialog opens.
  useEffect(() => {
    if (!prodOpen) {
      setPreflight(null);
      return;
    }
    setPreflightLoading(true);
    api.get("/app-mode/preflight")
      .then((r) => setPreflight(r.data))
      .catch((e) => {
        toast.error(apiErrorMessage(e, "Gagal memuat pemeriksaan pra-Production."));
        setPreflight({ error: true });
      })
      .finally(() => setPreflightLoading(false));
  }, [prodOpen]);

  const handleReset = async () => {
    setBusy(true);
    try {
      const r = await api.post("/app-mode/reset-simulasi", { confirmation_phrase: resetPhrase });
      setResetSummary(r.data);  // GAP-2: show detailed summary
      toast.success(`Data simulasi berhasil di-reset (${r.data.total_rows_deleted} baris, ${r.data.total_files_deleted} file)`);
      setResetOpen(false);
      setResetPhrase("");
      refreshAppMode();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal reset data simulasi."));
    } finally {
      setBusy(false);
    }
  };

  const handleActivateProduction = async () => {
    setBusy(true);
    try {
      await api.post("/app-mode/activate-production", { confirmation_phrase: prodPhrase });
      toast.success("Production Mode diaktifkan");
      setProdOpen(false);
      setProdPhrase("");
      refreshAppMode();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.preflight) {
        // Hard-gate failure — refresh preflight display.
        setPreflight(detail.preflight);
      }
      toast.error(apiErrorMessage(e, "Gagal mengaktifkan Production."));
    } finally {
      setBusy(false);
    }
  };

  // Gate: allow phrase only when preflight all_passed (backup PENDING accepted).
  const preflightBlocked = preflight && !preflight.all_passed && preflight.failed_checks?.length > 0;

  return (
    <div className="max-w-3xl space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Pengaturan</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Atur mode aplikasi: UAT/Trial (data simulasi) atau Production (data real).
        </p>
      </div>

      <KartuAlarmUji />
      <KartuDelegasiAktif />
      <KartuDokuDpPelunasan />
      {/* K210 BUSINESS-76: saklar DOKU per cabang — Owner melihat & mengubah semua cabang. */}
      <SaklarDokuCabang peran="OWNER" />
      {/* K217: cara bayar yang ditawarkan tautan DOKU (QRIS + Virtual Account) — Owner per metode. */}
      <KartuMetodeDoku />

      <Card className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Mode saat ini</div>
            <Badge
              data-testid="current-mode-badge"
              className={`mt-1 text-base px-3 py-1 ${isBeku ? "bg-danger text-danger-foreground" : isProduction ? "bg-success text-success-foreground" : "bg-info text-info-foreground"}`}
            >
              {appMode?.mode || "UAT"}
            </Badge>
          </div>
          {isProduction && (
            <div className="text-right text-xs text-muted-foreground" data-testid="production-meta">
              <div>Aktif sejak: {formatDateTime(appMode?.activated_at)}</div>
            </div>
          )}
        </div>

        {!isProduction && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded border p-3 text-sm" data-testid="kartu-snapshot">
            <span className="text-muted-foreground">
              Sebelum Reset: buat cadangan data + berkas di Supabase, lalu catat di sini. Pre-flight Production
              memeriksa snapshot dicatat SEBELUM Reset terakhir.
              {appMode?.pre_reset_snapshot_at && <> Terakhir dicatat: {formatDateTime(appMode.pre_reset_snapshot_at)}.</>}
            </span>
            <Button variant="outline" size="sm" disabled={busy} data-testid="catat-snapshot-btn"
                    onClick={async () => {
                      const note = await mintaTeks("Catatan snapshot (mis. nama/ID backup di Supabase):", { minPanjang: 3 });
                      if (!note) return;
                      setBusy(true);
                      try { await api.post("/app-mode/snapshot-recorded", { note }); toast.success("Snapshot dicatat."); refreshAppMode(); }
                      catch (e) { toast.error(apiErrorMessage(e, "Gagal mencatat snapshot.")); }
                      finally { setBusy(false); }
                    }}>
              Catat snapshot sudah dibuat
            </Button>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-3 pt-2">
          <Dialog open={resetOpen} onOpenChange={setResetOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="open-reset-dialog"
                variant="outline"
                disabled={isProduction}
                title={isProduction ? "Reset Data Simulasi hanya dapat dilakukan di mode UAT/Trial" : ""}
              >
                <AlertTriangle size={16} className="mr-2" />
                Reset Data Simulasi
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="reset-dialog">
              <DialogHeader>
                <DialogTitle>Reset Data Simulasi</DialogTitle>
                <DialogDescription>
                  Tindakan ini akan menghapus SELURUH data simulasi (transaksional) DAN file
                  di object storage. Master data (region, store, role, whitelist, user profile,
                  audit, app mode, price list, raw material, vendors, dll) TIDAK dihapus.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2">
                <label className="text-sm">Ketik <code className="px-1 bg-muted rounded">RESET</code> untuk konfirmasi:</label>
                <Input
                  data-testid="reset-phrase-input"
                  value={resetPhrase}
                  onChange={(e) => setResetPhrase(e.target.value)}
                  placeholder="RESET"
                />
              </div>
              <DialogFooter>
                <Button
                  data-testid="confirm-reset-btn"
                  variant="destructive"
                  disabled={resetPhrase !== "RESET" || busy}
                  onClick={handleReset}
                >
                  {busy ? "Memproses…" : "Konfirmasi Reset"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={prodOpen} onOpenChange={setProdOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="open-production-dialog"
                disabled={isProduction}
                title={isProduction ? "Sudah Production" : ""}
              >
                <Power size={16} className="mr-2" />
                Aktifkan Production Mode
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="production-dialog" className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Aktifkan Production Mode</DialogTitle>
                <DialogDescription>
                  Setelah Production aktif, Reset Data Simulasi tidak dapat dijalankan.
                  Pre-flight check di bawah wajib PASS.
                </DialogDescription>
              </DialogHeader>

              {/* Pre-flight preview — WP-0b GAP-3b */}
              <div className="space-y-2" data-testid="preflight-preview">
                <div className="text-sm font-medium">Pre-flight Checklist</div>
                {preflightLoading && <div className="text-sm text-muted-foreground">Loading…</div>}
                {preflight && !preflightLoading && (
                  <div className="border rounded p-3 space-y-2 text-sm bg-muted/50">
                    {preflight.checks?.map((c) => {
                      const Icon = c.passed === true ? CheckCircle2 : c.passed === false ? XCircle : Clock;
                      const color = c.passed === true ? "text-success" : c.passed === false ? "text-danger" : "text-warning";
                      return (
                        <div key={c.name} data-testid={`preflight-check-${c.name}`} className="flex gap-2">
                          <Icon size={16} className={`${color} mt-0.5 flex-shrink-0`} />
                          <div className="flex-1">
                            <div className="font-medium">{c.name}</div>
                            <div className="text-muted-foreground text-xs">{c.detail}</div>
                          </div>
                        </div>
                      );
                    })}
                    {preflight.failed_checks?.length > 0 && (
                      <div className="mt-2 text-xs text-danger-soft-foreground border-t pt-2" data-testid="preflight-failed-list">
                        Gagal: {preflight.failed_checks.join(", ")}
                      </div>
                    )}
                    {preflight.pending_checks?.length > 0 && (
                      <div className="mt-1 text-xs text-warning-soft-foreground" data-testid="preflight-pending-list">
                        Pending (dianggap PASS untuk saat ini): {preflight.pending_checks.join(", ")}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-sm">Ketik <code className="px-1 bg-muted rounded">PRODUCTION</code> untuk konfirmasi:</label>
                <Input
                  data-testid="production-phrase-input"
                  value={prodPhrase}
                  onChange={(e) => setProdPhrase(e.target.value)}
                  placeholder="PRODUCTION"
                  disabled={preflightBlocked}
                />
                {preflightBlocked && (
                  <div className="text-xs text-danger" data-testid="preflight-block-note">
                    Aktivasi diblokir karena ada check gagal. Perbaiki dulu sebelum lanjut.
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button
                  data-testid="confirm-production-btn"
                  disabled={prodPhrase !== "PRODUCTION" || busy || preflightBlocked}
                  onClick={handleActivateProduction}
                >
                  {busy ? "Memproses…" : "Konfirmasi Aktivasi"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </Card>

      {isProduction && <KartuBeku isBeku={isBeku} appMode={appMode} refreshAppMode={refreshAppMode} />}

      {/* Detailed reset summary — WP-0b GAP-2 */}
      {resetSummary && (
        <Card className="p-6 space-y-4" data-testid="reset-summary-card">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">Ringkasan Reset Terakhir</h2>
              <p className="text-xs text-muted-foreground mt-1">Detail hasil operasi Reset Data Simulasi.</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setResetSummary(null)} data-testid="close-reset-summary">Tutup</Button>
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="border rounded p-3 bg-muted/50">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Total Baris Dihapus</div>
              <div className="text-2xl font-bold text-foreground" data-testid="summary-rows-deleted">{resetSummary.total_rows_deleted}</div>
            </div>
            <div className="border rounded p-3 bg-muted/50">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Total File Dihapus</div>
              <div className="text-2xl font-bold text-foreground" data-testid="summary-files-deleted">{resetSummary.total_files_deleted}</div>
            </div>
          </div>

          {resetSummary.files_failed?.length > 0 && (
            <div className="border border-warning-soft-border bg-warning-soft rounded p-3 space-y-1 text-xs" data-testid="summary-files-failed">
              <div className="font-medium text-warning-soft-foreground">⚠️ File Gagal Dihapus ({resetSummary.files_failed.length})</div>
              <ul className="list-disc list-inside space-y-0.5 text-warning-soft-foreground">
                {resetSummary.files_failed.slice(0, 10).map((f, i) => (
                  <li key={i}><code>{f.bucket}/{f.object_key}</code> — {f.error}</li>
                ))}
                {resetSummary.files_failed.length > 10 && <li>… (+{resetSummary.files_failed.length - 10} lagi)</li>}
              </ul>
            </div>
          )}

          <div>
            <Button variant="link" size="sm" onClick={() => setShowTruncated(!showTruncated)} data-testid="toggle-truncated">
              {showTruncated ? "▲" : "▼"} Detail per tabel ({Object.keys(resetSummary.tables_truncated || {}).length})
            </Button>
            {showTruncated && (
              <div className="mt-2 border rounded p-2 text-xs bg-muted/50 max-h-64 overflow-y-auto" data-testid="truncated-detail">
                <table className="w-full">
                  <tbody>
                    {Object.entries(resetSummary.tables_truncated || {})
                      .sort((a, b) => b[1] - a[1])
                      .map(([t, c]) => (
                        <tr key={t}><td className="py-0.5 font-mono">{t}</td><td className="py-0.5 text-right pl-4">{c}</td></tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div>
            <Button variant="link" size="sm" onClick={() => setShowProtected(!showProtected)} data-testid="toggle-protected">
              {showProtected ? "▲" : "▼"} Master data (protected — TIDAK dihapus)
            </Button>
            {showProtected && (
              <div className="mt-2 border rounded p-3 text-xs bg-info-soft" data-testid="protected-detail">
                <div className="text-info-soft-foreground mb-2">{resetSummary.protected_review_note}</div>
                <table className="w-full">
                  <tbody>
                    {Object.entries(resetSummary.protected_tables_row_counts || {})
                      .sort((a, b) => (typeof b[1] === "number" ? b[1] : 0) - (typeof a[1] === "number" ? a[1] : 0))
                      .map(([t, c]) => (
                        <tr key={t}>
                          <td className="py-0.5 font-mono">{t}</td>
                          <td className="py-0.5 text-right pl-4">{typeof c === "number" ? c : <span className="text-danger">{c}</span>}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {resetSummary.storage_residue?.length > 0 && (
            <div className="text-xs text-muted-foreground border-t pt-2" data-testid="summary-storage-residue">
              <div className="font-medium mb-1">Catatan storage:</div>
              <ul className="list-disc list-inside space-y-0.5">
                {resetSummary.storage_residue.map((n, i) => <li key={i}>{n}</li>)}
              </ul>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
